import { type ReactElement } from 'react';
import type { dwrClassesById } from '@/data/classes';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import type { Character } from '@/schemas/character';
import type { Move } from '@/schemas/move';
import {
  getAvailableAdvancedMoves,
  getAvailableCommonMoves,
  getReplacedMoves,
  getStatCapForLevelUp,
} from '@/services/levelUp';
import { LevelUpStepPastTen } from './LevelUpStepPastTen';
import { LevelUpStepStat } from './LevelUpStepStat';
import { LevelUpStepMove } from './LevelUpStepMove';
import { LevelUpStepGranted } from './LevelUpStepGranted';
import { LevelUpStepMulticlass } from './LevelUpStepMulticlass';
import { LevelUpStepArchetype } from './LevelUpStepArchetype';
import { LevelUpStepSpells } from './LevelUpStepSpells';
import { LevelUpStepConfirm } from './LevelUpStepConfirm';
import { buildAllSpellRefs, getActiveSpellGrantsForLevelUp } from '@/services/spellGrants';
import type { LevelUpDraft, LevelUpStepId } from './types';

/**
 * Routing degli step del `LevelUpFlow`. Da PR 10 tutti i 9 step sono
 * "puri body" senza bottoni interni — la navigazione è centralizzata in
 * `<StepNav>` esterno renderizzato da `LevelUpFlowPage`.
 */

export interface LevelUpFlowStepsProps {
  step: LevelUpStepId;
  character: Character;
  classDef: NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;
  nextLevel: number;
  draft: LevelUpDraft;
  setDraft: (d: LevelUpDraft) => void;
  needsGrantedStep: boolean;
  needsMulticlassStep: boolean;
  needsArchetypeStep: boolean;
  needsSpellsStep: boolean;
  /** D10 / FI-11: true se il PG sta passando da L10 a L11. */
  needsPastTenStep: boolean;
  moveDef: Move | undefined;
  applyError: string | null;
}

type StepRenderer = (props: LevelUpFlowStepsProps) => ReactElement | null;

const stepRenderers: Record<LevelUpStepId, StepRenderer> = {
  intro: (p) => <StepIntroBody {...p} />,
  pastTen: (p) => (p.needsPastTenStep ? <PastTenStep {...p} /> : null),
  stat: (p) => <StatStep {...p} />,
  move: (p) => <MoveStep {...p} />,
  granted: (p) => (p.needsGrantedStep ? <GrantedStep {...p} /> : null),
  multiclass: (p) => (p.needsMulticlassStep ? <MulticlassStep {...p} /> : null),
  archetype: (p) => (p.needsArchetypeStep ? <ArchetypeStep {...p} /> : null),
  spells: (p) => (p.needsSpellsStep ? <SpellsStep {...p} /> : null),
  confirm: (p) => <ConfirmStep {...p} />,
};

export function LevelUpFlowSteps(props: LevelUpFlowStepsProps): ReactElement | null {
  return stepRenderers[props.step](props);
}

// ---------------------------------------------------------------------------
// Step renderers
// ---------------------------------------------------------------------------

/**
 * Step intro — PR 8 (decisione E1): riskinato con `<SectionHeader>` + 2
 * paragrafi informativi. Niente bottone interno: il "Procedi" è il pulsante
 * Avanti dello `<StepNav>` esterno renderizzato da `<LevelUpFlowPage>`.
 */
function StepIntroBody({ character, nextLevel }: LevelUpFlowStepsProps) {
  return (
    <section>
      <SectionHeader
        title="Stai per programmare il prossimo livello"
        sub={`Stai costruendo il livello ${nextLevel} del personaggio. Sceglierai una Caratteristica da aumentare di +1 (cap 18) e una nuova mossa. Se è il primo passaggio (L1 → L2) puoi opzionalmente scegliere Infanzia Complessa per ottenere un secondo archetipo.`}
      />
      <div className="text-ink-soft mt-4 space-y-3 text-[0.95rem] leading-relaxed">
        <p>
          La programmazione del livello non consuma PE (
          <span className="font-semibold">{character.xp}</span> attuali): sarà il futuro «giocabile»
          a sottrarli quando attiverai il livello al tavolo.
        </p>
      </div>
    </section>
  );
}

/** **PR 9**: step pastTen riskinato — no bottoni interni, no onNext/onBack. */
function PastTenStep({ draft, setDraft }: LevelUpFlowStepsProps) {
  return (
    <LevelUpStepPastTen
      selected={draft.pastTen}
      onSelect={(choice) => setDraft({ ...draft, pastTen: choice })}
    />
  );
}

/** **PR 8**: step stat riskinato — no bottoni interni, no onNext/onBack. */
function StatStep({ character, draft, setDraft }: LevelUpFlowStepsProps) {
  return (
    <LevelUpStepStat
      character={character}
      selected={draft.statIncreased}
      onSelect={(statId) => setDraft({ ...draft, statIncreased: statId })}
      getStatCap={() => getStatCapForLevelUp(character)}
    />
  );
}

/** **PR 8**: step move riskinato — no bottoni interni, no onNext/onBack. */
function MoveStep({ character, classDef, nextLevel, draft, setDraft }: LevelUpFlowStepsProps) {
  return (
    <LevelUpStepMove
      character={character}
      classDef={classDef}
      targetLevel={nextLevel}
      moveAdded={draft.moveAdded}
      onMoveChange={(moveAdded) =>
        setDraft({
          ...draft,
          moveAdded,
          grantedChoice: undefined,
          multiclassPick: undefined,
        })
      }
      getAvailableAdvancedMoves={() => getAvailableAdvancedMoves(character, nextLevel, classDef)}
      getAvailableCommonMoves={() => getAvailableCommonMoves(character, nextLevel)}
      getReplacedMoves={(move) => getReplacedMoves(move, character)}
    />
  );
}

/** **PR 9**: step granted riskinato — no bottoni interni. */
function GrantedStep(p: LevelUpFlowStepsProps) {
  const { character, classDef, draft, setDraft, moveDef } = p;
  if (!moveDef) return null;
  return (
    <LevelUpStepGranted
      character={character}
      classDef={classDef}
      moveDef={moveDef}
      granted={draft.grantedChoice}
      onGrantedChange={(grantedChoice) => setDraft({ ...draft, grantedChoice })}
    />
  );
}

/** **PR 9**: step multiclass riskinato — no bottoni interni. */
function MulticlassStep(p: LevelUpFlowStepsProps) {
  const { character, classDef, draft, setDraft, moveDef } = p;
  if (!moveDef) return null;
  return (
    <LevelUpStepMulticlass
      character={character}
      classDef={classDef}
      moveDef={moveDef}
      pick={draft.multiclassPick}
      onPickChange={(multiclassPick) => setDraft({ ...draft, multiclassPick })}
    />
  );
}

/** **PR 9**: step archetype riskinato — no bottoni interni.
 *  **FI-80 (2026-05-24)**: propaga setup compilato della mossa-archetipo
 *  del 2° archetipo + setter. Reset del setup al cambio di archetype id
 *  (il vecchio setup non vale per la nuova mossa-archetipo). */
function ArchetypeStep(p: LevelUpFlowStepsProps) {
  const { character, classDef, draft, setDraft } = p;
  return (
    <LevelUpStepArchetype
      character={character}
      classDef={classDef}
      selected={draft.secondaryArchetypeId}
      onSelect={(secondaryArchetypeId) =>
        setDraft({ ...draft, secondaryArchetypeId, secondaryArchetypeSetup: undefined })
      }
      archetypeSetup={draft.secondaryArchetypeSetup}
      onArchetypeSetupChange={(secondaryArchetypeSetup) =>
        setDraft({ ...draft, secondaryArchetypeSetup })
      }
    />
  );
}

/** **PR 10**: step spells riskinato — no bottoni interni. */
function SpellsStep(p: LevelUpFlowStepsProps) {
  const { character, nextLevel, draft, setDraft } = p;
  return (
    <LevelUpStepSpells
      character={character}
      targetLevel={nextLevel}
      moveAdded={draft.moveAdded}
      {...(draft.grantedChoice !== undefined ? { grantedChoice: draft.grantedChoice } : {})}
      {...(draft.multiclassPick !== undefined ? { multiclassPick: draft.multiclassPick } : {})}
      selections={draft.spellSelections}
      onSelectionsChange={(spellSelections) => setDraft({ ...draft, spellSelections })}
    />
  );
}

/**
 * **PR 10**: step confirm riskinato — il button "Conferma e applica L{N}"
 * è ora delegato a `<StepNav isLast lastLabel>` esterno renderizzato da
 * `LevelUpFlowPage`. Nessun `onConfirm`/`onBack` interno.
 */
function ConfirmStep(p: LevelUpFlowStepsProps) {
  const { character, classDef, draft, moveDef, applyError } = p;
  const spellsAdded = buildConfirmSpellsAdded(p);
  return (
    <LevelUpStepConfirm
      character={character}
      classDef={classDef}
      {...(draft.statIncreased !== undefined ? { statIncreased: draft.statIncreased } : {})}
      {...(draft.moveAdded !== undefined ? { moveAdded: draft.moveAdded } : {})}
      {...(moveDef !== undefined ? { moveDef } : {})}
      {...(draft.secondaryArchetypeId !== undefined
        ? { secondaryArchetypeId: draft.secondaryArchetypeId }
        : {})}
      spellsAdded={spellsAdded}
      {...(draft.grantedChoice !== undefined ? { grantedChoice: draft.grantedChoice } : {})}
      {...(draft.pastTen !== undefined ? { pastTen: draft.pastTen } : {})}
      error={applyError}
    />
  );
}

function buildConfirmSpellsAdded(p: LevelUpFlowStepsProps): readonly { id: string }[] {
  if (!p.draft.moveAdded) return [];
  const grantsForConfirm = getActiveSpellGrantsForLevelUp(
    p.character.classId,
    p.character.moves,
    p.draft.moveAdded,
  );
  return buildAllSpellRefs(grantsForConfirm, p.draft.spellSelections);
}
